<template>
  <v-sheet style="min-width:95vw; max-width:95vw">
    <v-tabs
      v-model="activeTab"
      color="primary"
      class="mt-2"
    >
      <v-tab value="redemption-settings" prepend-icon="mdi-auto-upload">
        Redemption Settings
      </v-tab>
      <v-tab value="coming-soon-1" prepend-icon="mdi-clock-outline">
        Coming Soon
      </v-tab>
      <v-tab value="coming-soon-2" prepend-icon="mdi-clock-outline">
        Coming Soon
      </v-tab>
    </v-tabs>
    <v-divider></v-divider>
    <v-window v-model="activeTab">
      <v-window-item value="redemption-settings">
        <v-tabs
          v-model="redemptionSettingsTab"
          color="primary"
          class="mt-2 px-2"
          aria-label="Redemption settings sections"
        >
          <v-tab
            value="redemption-settings-detail"
            prepend-icon="mdi-auto-upload"
            tabindex="0"
            aria-label="Redemption Settings"
          >
            Redemption Settings
          </v-tab>
          <v-tab
            value="hedge-rates"
            prepend-icon="mdi-chart-timeline-variant"
            tabindex="0"
            aria-label="Hedge Rates"
          >
            Hedge Rates
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>
        <v-window v-model="redemptionSettingsTab">
          <v-window-item value="redemption-settings-detail">
            <v-card flat class="pa-6">
              <v-card-title class="mb-4">Redemption Settings</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12">
                    <v-sheet border rounded class="pa-4">
                      <div class="text-subtitle-1 font-weight-medium mb-4">Last Submission</div>
                      <div class="text-body-1">
                        {{ lastAutoRedemptionPeriod }}
                      </div>
                      <v-switch
                        v-if="systemStore.userDetail.superUser"
                        v-model="openAutoRedemptions"
                        label="Open Redemptions"
                        color="blue"
                        class="mt-4"
                        @update:modelValue="autoRedemptionStateChanged"
                      ></v-switch>
                    </v-sheet>
                  </v-col>
                  <v-col cols="12">
                    <v-sheet border rounded class="pa-4">
                      <div class="text-subtitle-1 font-weight-medium mb-4">EVoucher Expiry</div>
                      <div class="d-flex align-center ga-4 flex-wrap">
                        <v-select
                          v-model="selectedExpiryQuarter"
                          :items="quarters"
                          label="Select Quarter"
                          variant="outlined"
                          density="compact"
                          hide-details
                          style="max-width: 220px;"
                          aria-label="Select quarter for EVoucher expiry update"
                        ></v-select>
                        <v-btn
                          variant="outlined"
                          prepend-icon="mdi-update"
                          :disabled="!selectedExpiryQuarter"
                          @click="confirmDialog = true"
                          @keydown.enter="confirmDialog = true"
                          aria-label="Update expired EVouchers for selected quarter"
                          tabindex="0"
                        >
                          Update Expired EVouchers
                        </v-btn>
                      </div>
                    </v-sheet>
                  </v-col>
                  <v-col cols="12">
                    <v-sheet border rounded class="pa-4">
                      <div class="text-subtitle-1 font-weight-medium mb-4">Manual Single Auto Redemption</div>
                      <div class="d-flex align-center ga-4 flex-wrap mb-4">
                        <v-select
                          v-model="manualSearchQuarter"
                          :items="quarters"
                          label="Select Quarter"
                          variant="outlined"
                          density="compact"
                          hide-details
                          style="max-width: 220px;"
                          aria-label="Select quarter for manual auto redemption search"
                        ></v-select>
                        <v-text-field
                          v-model="manualSearchAffinityId"
                          label="Partner Affinity Id"
                          variant="outlined"
                          density="compact"
                          hide-details
                          style="max-width: 220px;"
                          aria-label="Partner Affinity Id for manual auto redemption search"
                          @keydown.enter="searchEVoucherControlRecords"
                        ></v-text-field>
                        <v-btn
                          variant="outlined"
                          prepend-icon="mdi-magnify"
                          :disabled="!manualSearchQuarter || !manualSearchAffinityId"
                          :loading="manualSearchLoading"
                          @click="searchEVoucherControlRecords"
                          @keydown.enter="searchEVoucherControlRecords"
                          aria-label="Search EVoucher Control records"
                          tabindex="0"
                        >
                          Search
                        </v-btn>
                      </div>
                      <v-data-table
                        v-if="manualSearchPerformed"
                        :headers="manualSearchHeaders"
                        :items="manualSearchResults"
                        :items-per-page="10"
                        :items-per-page-options="[5, 10, 25, 50, -1]"
                        item-key="id"
                        density="compact"
                        class="border"
                        role="table"
                        aria-label="EVoucher Control search results"
                      >
                        <template v-slot:item.usdAmount="{ item }">
                          {{ item.usdAmount != null ? Number(item.usdAmount).toFixed(2) : '' }}
                        </template>
                        <template v-slot:item.localAmount="{ item }">
                          {{ item.localAmount != null ? Number(item.localAmount).toFixed(2) : '' }}
                        </template>
                        <template v-slot:item.eVoucherExpiryDate="{ item }">
                          {{ item.eVoucherExpiryDate ? $filters.toNiceDate(item.eVoucherExpiryDate) : '' }}
                        </template>
                        <template v-slot:item.actions="{ item }">
                          <v-btn
                            v-if="item.transactionTypeLink === 16"
                            variant="outlined"
                            size="small"
                            density="compact"
                            prepend-icon="mdi-cash-check"
                            :loading="redeemLoading && redeemTargetId === item.id"
                            @click="openRedeemDialog(item)"
                            @keydown.enter="openRedeemDialog(item)"
                            aria-label="Redeem opening balance record"
                            tabindex="0"
                          >
                            Redeem
                          </v-btn>
                        </template>
                        <template v-slot:no-data>
                          No EVoucher Control records found for the selected Quarter and Partner Affinity Id.
                        </template>
                      </v-data-table>
                    </v-sheet>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-window-item>
          <v-window-item value="hedge-rates">
            <v-card flat class="pa-6">
              <v-card-title class="mb-4">Hedge Rates</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12">
                    <v-sheet border rounded class="pa-4">
                      <div class="d-flex align-center ga-4 flex-wrap mb-4">
                        <v-select
                          v-model="hedgeRateQuarter"
                          :items="quarters"
                          label="Select Quarter"
                          variant="outlined"
                          density="compact"
                          hide-details
                          style="max-width: 220px;"
                          aria-label="Select quarter for hedge rates"
                        ></v-select>
                        <v-text-field
                          v-model="hedgeRateSearch"
                          label="Search"
                          prepend-inner-icon="mdi-magnify"
                          variant="outlined"
                          density="compact"
                          hide-details
                          clearable
                          style="max-width: 280px;"
                          aria-label="Search hedge rate records"
                          aria-describedby="hedge-rate-search-help"
                        >
                          <template v-slot:label>
                            <span id="hedge-rate-search-label">Search</span>
                          </template>
                        </v-text-field>
                        <div id="hedge-rate-search-help" class="sr-only">
                          Type to filter the hedge rate table
                        </div>
                      </div>
                      <v-data-table
                        :headers="hedgeRateHeaders"
                        :items="hedgeRates"
                        :search="hedgeRateSearch"
                        :loading="hedgeRatesLoading"
                        :items-per-page="10"
                        :items-per-page-options="[5, 10, 25, 50, -1]"
                        item-key="id"
                        density="compact"
                        class="border"
                        role="table"
                        aria-label="Hedge rate records"
                      >
                        <template v-slot:item.importDate="{ item }">
                          {{ item.importDate ? $filters.toNiceDate(item.importDate) : '' }}
                        </template>
                        <template v-slot:item.rate="{ item }">
                          {{ item.rate != null ? Number(item.rate) : '' }}
                        </template>
                        <template v-slot:no-data>
                          {{ !hedgeRateQuarter ? 'Select a quarter to view hedge rates.' : hedgeRateSearch ? 'No hedge rates match the search.' : 'No hedge rates found for the selected quarter.' }}
                        </template>
                      </v-data-table>
                    </v-sheet>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-window-item>
        </v-window>
      </v-window-item>
      <v-window-item value="coming-soon-1">
        <v-card flat class="pa-6">
          <v-card-title class="mb-4">Coming Soon</v-card-title>
          <v-card-text>
            <div class="text-body-1 text-medium-emphasis">This section is not yet available.</div>
          </v-card-text>
        </v-card>
      </v-window-item>
      <v-window-item value="coming-soon-2">
        <v-card flat class="pa-6">
          <v-card-title class="mb-4">Coming Soon</v-card-title>
          <v-card-text>
            <div class="text-body-1 text-medium-emphasis">This section is not yet available.</div>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>
  </v-sheet>
  <v-dialog v-model="confirmDialog" width="450">
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>Confirm Update</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pa-4">
        Update EVoucher status to <strong>Expired</strong> for all EVouchers in quarter <strong>{{ selectedExpiryQuarter }}</strong> where:
        <ul>
          <li>Status is <strong>Active</strong></li>
          <li>EVoucher status is <strong>Rewards Redeemed</strong></li>
          <li>Expiry date is before today</li>
        </ul>
      </v-card-text>
      <v-card-actions class="border-t-thin">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="confirmDialog = false">Cancel</v-btn>
        <v-btn variant="outlined" color="primary" @click="confirmUpdateExpiredEVouchers">Confirm</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="redeemDialog" width="700" persistent>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>Redeem</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pa-4">
        <div v-if="redeemOptionsLoading" class="text-body-1">Loading redemption options...</div>
        <div v-else-if="redeemBalanceIsZero">
          <div class="text-body-1 mb-2">
            Balance is <strong>USD {{ Number(redeemOptions.balance || 0).toFixed(2) }}</strong>.
          </div>
          <div class="text-body-1">Nothing available to redeem for this Opening Balance record.</div>
        </div>
        <div v-else>
          <div class="mb-4">
            <div class="text-body-1"><strong>Partner:</strong> {{ redeemOptions.partnerName }} ({{ redeemOptions.partnerAffinityId }})</div>
            <div class="text-body-1"><strong>Quarter:</strong> {{ redeemOptions.quarter }}</div>
            <div class="text-body-1"><strong>Opening Balance:</strong> USD {{ Number(redeemOptions.openingBalance || 0).toFixed(2) }}</div>
            <div class="text-body-1"><strong>EVouchers Total:</strong> USD {{ Number(redeemOptions.rewardsRedeemed || 0).toFixed(2) }}</div>
            <div class="text-body-1"><strong>Balance:</strong> USD {{ Number(redeemOptions.balance || 0).toFixed(2) }}</div>
          </div>
          <v-radio-group
            v-model="redeemMode"
            label="Choose redemption type"
            hide-details
            class="mb-4"
            aria-label="Choose redemption type"
          >
            <v-radio label="Redeem manually" value="Manual"></v-radio>
            <v-radio label="Auto redeem" value="Auto"></v-radio>
          </v-radio-group>
          <div v-if="redeemMode === 'Manual'">
            <v-select
              v-model="selectedManualDistiId"
              :items="redeemOptions.manualDistributors || []"
              item-title="description"
              item-value="distiId"
              label="Select Distributor"
              variant="outlined"
              density="compact"
              :rules="[v => !!v || 'Distributor is required']"
              aria-label="Select distributor for manual redemption"
            ></v-select>
            <div v-if="!(redeemOptions.manualDistributors || []).length" class="text-body-2 text-medium-emphasis">
              No active distributors found for this partner country.
            </div>
          </div>
          <div v-else>
            <div class="text-subtitle-2 mb-2">Preferred / Dominant distributors</div>
            <v-data-table
              :headers="autoDistributorHeaders"
              :items="redeemOptions.autoDistributors || []"
              :items-per-page="-1"
              hide-default-footer
              density="compact"
              class="border"
              item-key="distiId"
              role="table"
              aria-label="Preferred and Dominant distributors"
            >
              <template v-slot:no-data>
                No Preferred or Dominant distributor found for this partner.
              </template>
            </v-data-table>
            <div class="text-body-2 text-medium-emphasis mt-2">
              Distributor is assigned automatically (Preferred first, otherwise Dominant).
            </div>
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="border-t-thin">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeRedeemDialog" aria-label="Close redeem dialog">
          {{ redeemBalanceIsZero ? 'Close' : 'Cancel' }}
        </v-btn>
        <v-btn
          v-if="!redeemBalanceIsZero && !redeemOptionsLoading"
          variant="outlined"
          color="primary"
          :disabled="!canContinueRedeem"
          @click="openRedeemConfirmDialog"
          aria-label="Continue to redemption confirmation"
        >
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="redeemConfirmDialog" width="500" persistent>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>Confirm Redemption</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pa-4">
        <div class="text-body-1 mb-2">
          Confirm <strong>{{ redeemMode === 'Manual' ? 'manual' : 'auto' }}</strong> redemption for:
        </div>
        <ul>
          <li>Partner: <strong>{{ redeemOptions.partnerName }}</strong> ({{ redeemOptions.partnerAffinityId }})</li>
          <li>Quarter: <strong>{{ redeemOptions.quarter }}</strong></li>
          <li>Balance: <strong>USD {{ Number(redeemOptions.balance || 0).toFixed(2) }}</strong></li>
          <li v-if="redeemMode === 'Manual'">
            Distributor: <strong>{{ selectedManualDistributorLabel }}</strong>
          </li>
          <li v-else>
            Distributor: <strong>{{ selectedAutoDistributorLabel }}</strong>
          </li>
        </ul>
      </v-card-text>
      <v-card-actions class="border-t-thin">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="redeemConfirmDialog = false" aria-label="Cancel redemption confirmation">Cancel</v-btn>
        <v-btn
          variant="outlined"
          color="primary"
          :loading="redeemSubmitting"
          @click="confirmRedeem"
          aria-label="Confirm redemption"
        >
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script setup>
  import { computed, onMounted, ref, watch } from 'vue'
  import { useSystemStore } from '@/stores/system'
  const systemStore = useSystemStore()
  const activeTab = ref('redemption-settings')
  const redemptionSettingsTab = ref('redemption-settings-detail')
  const openAutoRedemptions = ref(false)
  const lastAutoRedemptionPeriod = ref('')
  const quarters = ref([])
  const selectedExpiryQuarter = ref(null)
  const confirmDialog = ref(false)
  const manualSearchQuarter = ref(null)
  const manualSearchAffinityId = ref('')
  const manualSearchResults = ref([])
  const manualSearchLoading = ref(false)
  const manualSearchPerformed = ref(false)
  const redeemDialog = ref(false)
  const redeemConfirmDialog = ref(false)
  const redeemOptionsLoading = ref(false)
  const redeemLoading = ref(false)
  const redeemSubmitting = ref(false)
  const redeemTargetId = ref(null)
  const redeemMode = ref('Manual')
  const selectedManualDistiId = ref(null)
  const hedgeRateQuarter = ref(null)
  const hedgeRates = ref([])
  const hedgeRatesLoading = ref(false)
  const hedgeRateSearch = ref('')
  const hedgeRateHeaders = [
    { key: 'id', title: 'Id' },
    { key: 'quarter', title: 'Quarter' },
    { key: 'importDate', title: 'Import Date' },
    { key: 'status', title: 'Status' },
    { key: 'countryName', title: 'Country Name' },
    { key: 'countryCode', title: 'Country Code' },
    { key: 'currencyCode', title: 'Currency Code' },
    { key: 'rate', title: 'Rate' },
  ]
  const redeemOptions = ref({
    eVoucherControlId: 0,
    quarter: '',
    partnerAffinityId: 0,
    partnerName: '',
    partnerCountry: '',
    openingBalance: 0,
    rewardsRedeemed: 0,
    balance: 0,
    banked: 0,
    closingBalance: 0,
    manualDistributors: [],
    autoDistributors: [],
  })
  const manualSearchHeaders = [
    { key: 'id', title: 'Id' },
    { key: 'quarter', title: 'Quarter' },
    { key: 'partnerAffinityId', title: 'Partner Affinity Id' },
    { key: 'partnerName', title: 'Partner Name' },
    { key: 'partnerCountry', title: 'Partner Country' },
    { key: 'distributorAffinityId', title: 'Distributor Affinity Id' },
    { key: 'distributorName', title: 'Distributor Name' },
    { key: 'transactionType', title: 'Transaction Type' },
    { key: 'eVoucherNumber', title: 'EVoucher Number' },
    { key: 'usdAmount', title: 'USD Amount' },
    { key: 'localCurrency', title: 'Local Currency' },
    { key: 'localAmount', title: 'Local Amount' },
    { key: 'eVoucherStatus', title: 'EVoucher Status' },
    { key: 'eVoucherExpiryDate', title: 'Expiry Date' },
    { key: 'actions', title: 'Actions', sortable: false },
  ]
  const autoDistributorHeaders = [
    { key: 'allocation', title: 'Allocation' },
    { key: 'distiId', title: 'Affinity Id' },
    { key: 'distributorName', title: 'Distributor Name' },
    { key: 'distributorCountry', title: 'Country' },
    { key: 'email', title: 'Email' },
  ]
  const redeemBalanceIsZero = computed(() => {
    return !redeemOptionsLoading.value && Number(redeemOptions.value.balance || 0) <= 0
  })
  const canContinueRedeem = computed(() => {
    if (redeemMode.value === 'Manual') {
      return !!selectedManualDistiId.value
    }
    return (redeemOptions.value.autoDistributors || []).length > 0
  })
  const selectedManualDistributorLabel = computed(() => {
    const disti = (redeemOptions.value.manualDistributors || []).find(d => d.distiId === selectedManualDistiId.value)
    return disti ? disti.description : ''
  })
  const selectedAutoDistributorLabel = computed(() => {
    const preferred = (redeemOptions.value.autoDistributors || []).find(d => d.allocation === 'Preferred')
    const dominant = (redeemOptions.value.autoDistributors || []).find(d => d.allocation === 'Dominant')
    const selected = preferred || dominant
    if (!selected) {
      return 'None'
    }
    return `${selected.distributorName} (${selected.allocation})`
  })
  const getLastAutoRedemptionPeriod = () => {
    systemStore.getLastAutoRedemptionPeriod().then(result => {
      lastAutoRedemptionPeriod.value = result.responseData
    }).catch(error => {
      console.log('getLastAutoRedemptionPeriod', error)
    })
  }
  const autoRedemptionStateChanged = (value) => {
    systemStore.setAutoRedemptionState(value).then(result => {
      openAutoRedemptions.value = result.autoRedemptionState
      if (openAutoRedemptions.value === true) {
        systemStore.showMessage('Redemptions are OPEN', { color: 'success' })
      }
      else {
        systemStore.showMessage('Redemptions are CLOSED', { color: 'warning' })
      }
    }).catch(error => {
      console.log('autoRedemptionStateChanged', error)
    })
  }
  const confirmUpdateExpiredEVouchers = () => {
    confirmDialog.value = false
    systemStore.updateEVouchersAsExpired(selectedExpiryQuarter.value).then(result => {
      if (result.success) {
        systemStore.showMessage(`${result.updatedCount} EVoucher(s) updated successfully.`, { color: 'success' })
      } else {
        systemStore.showMessage('Failed to update EVouchers.', { color: 'error' })
      }
    }).catch(error => {
      console.log('updateEVouchersAsExpired', error)
    })
  }
  const searchEVoucherControlRecords = () => {
    if (!manualSearchQuarter.value || !manualSearchAffinityId.value) {
      return
    }
    manualSearchLoading.value = true
    manualSearchPerformed.value = true
    systemStore.getEVoucherControlRecords(manualSearchQuarter.value, manualSearchAffinityId.value).then(result => {
      if (result.success) {
        manualSearchResults.value = result.eVouchers || []
        if (!result.hasData) {
          systemStore.showMessage('No EVoucher Control records found.', { color: 'warning' })
        }
      } else {
        manualSearchResults.value = []
        systemStore.showMessage('Failed to search EVoucher Control records.', { color: 'error' })
      }
    }).catch(error => {
      manualSearchResults.value = []
      console.log('getEVoucherControlRecords', error)
      systemStore.showMessage('Failed to search EVoucher Control records.', { color: 'error' })
    }).finally(() => {
      manualSearchLoading.value = false
    })
  }
  const resetRedeemState = () => {
    redeemMode.value = 'Manual'
    selectedManualDistiId.value = null
    redeemOptions.value = {
      eVoucherControlId: 0,
      quarter: '',
      partnerAffinityId: 0,
      partnerName: '',
      partnerCountry: '',
      openingBalance: 0,
      rewardsRedeemed: 0,
      balance: 0,
      banked: 0,
      closingBalance: 0,
      manualDistributors: [],
      autoDistributors: [],
    }
  }
  const closeRedeemDialog = () => {
    redeemDialog.value = false
    redeemConfirmDialog.value = false
    redeemTargetId.value = null
    resetRedeemState()
  }
  const openRedeemDialog = (item) => {
    if (!item || item.transactionTypeLink !== 16) {
      return
    }
    resetRedeemState()
    redeemTargetId.value = item.id
    redeemDialog.value = true
    redeemOptionsLoading.value = true
    redeemLoading.value = true
    systemStore.getManualSingleAutoRedemptionOptions(item.id).then(result => {
      if (result.success) {
        redeemOptions.value = {
          eVoucherControlId: result.eVoucherControlId,
          quarter: result.quarter,
          partnerAffinityId: result.partnerAffinityId,
          partnerName: result.partnerName,
          partnerCountry: result.partnerCountry,
          openingBalance: result.openingBalance,
          rewardsRedeemed: result.rewardsRedeemed,
          balance: result.balance,
          banked: result.banked,
          closingBalance: result.closingBalance,
          manualDistributors: result.manualDistributors || [],
          autoDistributors: result.autoDistributors || [],
        }
      } else {
        systemStore.showMessage(result.messages?.[0] || 'Failed to load redemption options.', { color: 'error' })
        closeRedeemDialog()
      }
    }).catch(error => {
      console.log('getManualSingleAutoRedemptionOptions', error)
      systemStore.showMessage('Failed to load redemption options.', { color: 'error' })
      closeRedeemDialog()
    }).finally(() => {
      redeemOptionsLoading.value = false
      redeemLoading.value = false
    })
  }
  const openRedeemConfirmDialog = () => {
    if (!canContinueRedeem.value) {
      return
    }
    redeemConfirmDialog.value = true
  }
  const getHedgeRates = () => {
    if (!hedgeRateQuarter.value) {
      hedgeRates.value = []
      return
    }
    hedgeRatesLoading.value = true
    systemStore.getHedgeRates(hedgeRateQuarter.value).then(result => {
      if (result.success) {
        hedgeRates.value = result.hedgeRates || []
      } else {
        hedgeRates.value = []
        systemStore.showMessage(result.messages?.[0] || 'Failed to load hedge rates.', { color: 'error' })
      }
    }).catch(error => {
      hedgeRates.value = []
      console.log('getHedgeRates', error)
      systemStore.showMessage('Failed to load hedge rates.', { color: 'error' })
    }).finally(() => {
      hedgeRatesLoading.value = false
    })
  }
  watch(hedgeRateQuarter, () => {
    hedgeRateSearch.value = ''
  })
  watch([hedgeRateQuarter, redemptionSettingsTab], () => {
    if (redemptionSettingsTab.value === 'hedge-rates') {
      getHedgeRates()
    }
  })
  const confirmRedeem = () => {
    redeemSubmitting.value = true
    const distiId = redeemMode.value === 'Manual' ? selectedManualDistiId.value : 0
    systemStore.submitManualSingleAutoRedemption(
      redeemOptions.value.eVoucherControlId,
      redeemMode.value,
      distiId
    ).then(result => {
      if (result.success) {
        systemStore.showMessage(result.messages?.[0] || 'Redemption submitted successfully.', { color: 'success' })
        closeRedeemDialog()
        searchEVoucherControlRecords()
      } else {
        systemStore.showMessage(result.messages?.[0] || 'Redemption failed.', { color: 'error' })
      }
    }).catch(error => {
      console.log('submitManualSingleAutoRedemption', error)
      systemStore.showMessage('Redemption failed.', { color: 'error' })
    }).finally(() => {
      redeemSubmitting.value = false
      redeemConfirmDialog.value = false
    })
  }
  onMounted(() => {
    systemStore.getAutoRedemptionState().then(result => {
      openAutoRedemptions.value = result.autoRedemptionState
    }).catch(error => {
      console.log('getAutoRedemptionState', error)
    })
    systemStore.getQuarters().then(result => {
      if (result.success) {
        quarters.value = result.quarters.map(q => q.quarter)
        const currentRedemptionQuarter = result.quarters.find(q => q.currentRedemptionQuarter === true)
        hedgeRateQuarter.value = currentRedemptionQuarter?.quarter || result.quarters[0]?.quarter || null
      }
    }).catch(error => {
      console.log('getQuarters', error)
    })
    getLastAutoRedemptionPeriod()
  })
</script>
<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
